import { http, DEFAULT_TOKEN, getAuthToken } from './client';
import { useConfig } from '../hooks/useConfig';

/** EAC 项目基础信息。 */
export interface EacProjectItem {
  id: number;
  code: string;
  name: string;
  bac: number;
  eac: number;
  ac: number;
  estimatedHours: number;
  forecast: number;
  createdAt: string;
}

/** EAC 汇总报表行数据。 */
export interface EacReportSummaryItem {
  id: number;
  projectCode: string;
  job: string;
  name: string;
  hourlyRate: number;
  actualCost: number;
  estimatedCost: number;
  allocationRatio: number;
  status: string;
  actualHours: number;
  estimatedHours: number;
}

/** EAC 团队管理-项目人员数据。 */
export interface EacStaffItem {
  id: number;
  projectCode: string;
  job: string;
  name: string;
  hourlyRate: number;
  actualCost: number;
  estimatedCost: number;
  allocationRatio: number;
  status: 'Active' | 'Inactive' | string;
  createdAt: string;
}

/** EAC 团队管理-插入人员请求体。 */
export interface CreateEacStaffPayload {
  projectCode: string;
  job: string;
  name: string;
  hourlyRate: number;
  actualCost: number;
  estimatedCost: number;
}

export type RunEacProcessPayload = Record<string, any>;

interface EacStaffUpdateResponse {
  updated: number;
}

interface EacStaffCreateResponse {
  id: number;
}

const getRuntimeApiBaseUrl = (): string => {
  const { serverConfig } = useConfig();
  const baseUrl =
    (serverConfig as { holonBaseUrl?: string })?.holonBaseUrl ||
    'https://holon.aialign.cn';

  return baseUrl.replace(/\/$/, '');
};

/** 获取 EAC 项目列表。当前页面按产品要求仅使用第一条数据。 */
export async function getEacProjectList(): Promise<EacProjectItem[]> {
  const response = await http.get<EacProjectItem[]>(
    '/ai-api/holon/v1/eac/project'
  );
  return response.data || [];
}

/** 获取单个 EAC 项目详情。 */
export async function getEacProjectDetail(
  projectCode: string
): Promise<EacProjectItem> {
  const response = await http.get<EacProjectItem>(
    `/ai-api/holon/v1/eac/project/${projectCode}`
  );
  return response.data;
}

/**
 * 获取 EAC 汇总报表。
 * @param projectCode 项目编码，来源于项目信息接口
 */
export async function getEacReportSummary(
  projectCode: string
): Promise<EacReportSummaryItem[]> {
  const response = await http.get<EacReportSummaryItem[]>(
    '/ai-api/holon/v1/eac/report/summary',
    {
      params: { projectCode },
    }
  );
  return response.data || [];
}

/** 获取项目人员列表。 */
export async function getEacStaffList(
  projectCode: string
): Promise<EacStaffItem[]> {
  const response = await http.get<EacStaffItem[]>(
    '/ai-api/holon/v1/eac/staff',
    {
      params: { projectCode },
    }
  );
  return response.data || [];
}

/** 将项目人员标记为 Inactive。 */
export async function removeEacStaff(
  staffId: number
): Promise<EacStaffUpdateResponse> {
  const response = await http.put<EacStaffUpdateResponse>(
    `/ai-api/holon/v1/eac/staff/${staffId}/remove`
  );
  return response.data;
}

/** 将项目人员标记为 Active。 */
export async function reactivateEacStaff(
  staffId: number
): Promise<EacStaffUpdateResponse> {
  const response = await http.put<EacStaffUpdateResponse>(
    `/ai-api/holon/v1/eac/staff/${staffId}/reactivate`
  );
  return response.data;
}

/** 新增项目人员。 */
export async function createEacStaff(
  payload: CreateEacStaffPayload
): Promise<EacStaffCreateResponse> {
  const response = await http.post<EacStaffCreateResponse>(
    '/ai-api/holon/v1/eac/staff',
    payload
  );
  return response.data;
}

export async function runEacProcessStream(
  payload: RunEacProcessPayload,
  abortController?: AbortController
): Promise<ReadableStream<Uint8Array>> {
  const response = await fetch(
    `${getRuntimeApiBaseUrl()}/ai-api/holon/v1/processes/orchestrator`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/x-ndjson',
        Authorization: `Bearer ${DEFAULT_TOKEN}`,
      },
      body: JSON.stringify(payload),
      signal: abortController?.signal,
    }
  );

  if (!response.ok) {
    const errorText = await response.text().catch(() => '未知错误');
    throw new Error(`请求失败(${response.status})：${errorText}`);
  }

  if (!response.body) {
    throw new Error('响应体为空，无法读取流数据');
  }

  return response.body;
}

export async function adoptEacRecommendation(
  projectCode: string,
  payload: unknown
): Promise<void> {
  await http.post(`/ai-api/holon/v1/eac/project/${projectCode}/adopt`, payload);
}

/** 将项目数据恢复到最原始状态。 */
export async function restoreEacProject(projectCode: string): Promise<void> {
  await http.post(`/ai-api/holon/v1/eac/project/${projectCode}/adopt`, {
    project: {
      bac: 2000000,
      eac: 2000000,
    },
    staff: [
      {
        job: 'Intermediate Engineer',
        name: 'Katie Swift',
        hourly_rate: 1200,
        estimated_cost: 600000,
        allocation_ratio: 100,
      },
    ],
  });
}
